import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, Variants, Transition } from 'framer-motion';
import { Typography, Button as MuiButton } from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { useThemeContext } from './ThemeWrapper'; // Theme context

import Sidebar from './Sidebar';
import RightPanel from './RightPanel';

// Kanji tip item type
type KanjiTipItem = [string, string, string];
type KanjiTipSection = {
    title: string;
    data: KanjiTipItem[];
};

const Kanji101: React.FC = () => {
    const navigate = useNavigate();
    const { isDarkTheme } = useThemeContext();

    // Kanji tips data
    const kanji101Data: KanjiTipSection[] = [
        {
            title: 'Common Radicals',
            data: [
                ['人', 'hito', 'person radical'],
                ['水', 'mizu', 'water radical'],
                ['木', 'ki', 'tree radical'],
                ['口', 'kuchi', 'mouth radical'],
                ['手', 'te', 'hand radical'],
            ],
        },
        {
            title: 'Basic Numbers',
            data: [
                ['一', 'ichi', 'one'],
                ['二', 'ni', 'two'],
                ['三', 'san', 'three'],
                ['四', 'shi/yon', 'four'],
                ['五', 'go', 'five'],
            ],
        },
        {
            title: 'Time and Date',
            data: [
                ['日', 'nichi/hi', 'day, sun'],
                ['月', 'getsu/tsuki', 'month, moon'],
                ['年', 'nen', 'year'],
                ['時', 'ji', 'time, hour'],
            ],
        },
        {
            title: 'Directional Kanji',
            data: [
                ['東', 'higashi', 'east'],
                ['西', 'nishi', 'west'],
                ['南', 'minami', 'south'],
                ['北', 'kita', 'north'],
            ],
        },
        {
            title: 'Common Nouns',
            data: [
                ['本', 'hon', 'book, origin'],
                ['国', 'kuni', 'country'],
                ['学', 'gaku', 'study'],
                ['円', 'en', 'yen, circle'],
            ],
        },
    ];

    // Play audio for romaji
    const playAudio = (romaji: string) => {
        if (!romaji) return;
        const cleanRomaji = romaji.split('/')[0].toLowerCase().trim();
        const audio = new Audio(`${process.env.PUBLIC_URL}/audio/${cleanRomaji}.mp3`);
        audio.play().catch(err => {
            console.error(`Audio file not found: ${cleanRomaji}.mp3`, err);
        });
    };

    // Framer Motion animations
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    };

    const springTransition: Transition = {
        type: 'spring',
        stiffness: 100,
        damping: 10,
    };

    const itemVariants: Variants = {
        hidden: { opacity: 0, y: 20 },
        visible: { opacity: 1, y: 0, transition: springTransition },
    };

    return (
        <div className={`flex min-h-screen font-inter ${isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'}`}>
            <Sidebar />

            <main className={`flex-1 p-8 pb-20 md:p-12 overflow-y-auto ${isDarkTheme ? 'bg-black text-white' : 'bg-white text-black'}`}>
                {/* Mobile Back Button */}
                <motion.div
                    className="md:hidden absolute top-4 left-4 sm:top-8 sm:left-8 z-10 mt-8"
                    initial={{ x: -100, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ type: 'spring', stiffness: 100, damping: 10 }}
                >
                    <MuiButton
                        variant="contained"
                        startIcon={<ArrowLeft size={20} />}
                        onClick={() => navigate("/hiragana")}
                        className={`!font-bold !py-2 !px-4 !rounded-xl !shadow-md backdrop-blur-md
                            ${isDarkTheme
                                ? '!bg-gray-800/70 hover:!bg-gray-700/70 !text-white'
                                : '!bg-gray-200/70 hover:!bg-gray-300/70 !text-black'
                            }`}
                        component={motion.button}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        Back
                    </MuiButton>
                </motion.div>

                {/* Page Title */}
                <Typography variant="h3" component="h1" className="!font-extrabold mb-2">
                    Kanji Tips
                </Typography>
                <Typography variant="body1" component="p" className="text-lg mb-6 text-gray-700 dark:text-gray-300">
                    See all the tips about Kanji
                </Typography>

                {/* Kanji Sections */}
                <AnimatePresence mode="wait">
                    <motion.div
                        key="kanji-tips-sections"
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        variants={containerVariants}
                    >
                        {kanji101Data.map((section, sectionIndex) => (
                            <React.Fragment key={section.title || `section-${sectionIndex}`}>
                                <Typography
                                    variant="h5"
                                    component="h2"
                                    className="!font-extrabold mt-8 mb-4"
                                >
                                    {section.title}
                                </Typography>
                                <motion.div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                                    {section.data.map((item, itemIndex) => (
                                        <motion.div
                                            key={`${section.title}-${itemIndex}`}
                                            className={`p-4 rounded-xl shadow-lg border-2 border-transparent cursor-pointer transition-transform duration-200 
                                                ${isDarkTheme
                                                    ? 'bg-zinc-900 hover:border-emerald-400'
                                                    : 'bg-white hover:border-emerald-600'
                                                }`}
                                            whileHover={{ scale: 1.05, y: -5 }}
                                            whileTap={{ scale: 0.95 }}
                                            variants={itemVariants}
                                            onClick={() => playAudio(item[1])}
                                        >
                                            <div className="text-3xl font-bold text-center mb-1">{item[0]}</div>
                                            <div className="text-sm text-center mb-1">{item[1]}</div>
                                            {item[2] && (
                                                <div className="text-xs text-center text-gray-600 dark:text-gray-400">
                                                    {item[2]}
                                                </div>
                                            )}
                                            <div className="h-1 bg-gray-300 rounded-full mt-2 dark:bg-gray-700">
                                                <div className="h-full w-0 bg-emerald-600 rounded-full transition-all duration-500 dark:bg-emerald-400"></div>
                                            </div>
                                        </motion.div>
                                    ))}
                                </motion.div>
                            </React.Fragment>
                        ))}
                    </motion.div>
                </AnimatePresence>
            </main>

            <RightPanel />
        </div>
    );
};

export default Kanji101;
